/**
 * Weather Service - Enterprise Command Center
 * 
 * Uses Open-Meteo API (100% FREE, no API key required)
 * Provides real-time weather data for field operations safety
 * 
 * API Documentation: https://open-meteo.com/
 */

export interface WeatherData {
    temperature: number;        // Fahrenheit
    windSpeed: number;          // mph
    windDirection: number;      // degrees
    weatherCode: number;        // WMO weather code
    precipitation: number;      // inches
    humidity: number;           // percentage
    visibility: number;         // miles
    isStormy: boolean;
    isSevere: boolean;
    description: string;
    icon: 'clear' | 'cloudy' | 'rain' | 'storm' | 'snow' | 'fog';
    timestamp: Date;
}

export interface WeatherAlert {
    id: string;
    type: 'lightning' | 'tornado' | 'flood' | 'heat' | 'cold' | 'wind';
    severity: 'watch' | 'warning' | 'emergency';
    title: string;
    description: string;
    expiresAt: Date;
}

// WMO Weather Code to description/icon mapping
const WMO_CODES: Record<number, { description: string; icon: WeatherData['icon'] }> = {
    0: { description: 'Clear sky', icon: 'clear' },
    1: { description: 'Mainly clear', icon: 'clear' },
    2: { description: 'Partly cloudy', icon: 'cloudy' },
    3: { description: 'Overcast', icon: 'cloudy' },
    45: { description: 'Foggy', icon: 'fog' },
    48: { description: 'Depositing rime fog', icon: 'fog' },
    51: { description: 'Light drizzle', icon: 'rain' },
    53: { description: 'Moderate drizzle', icon: 'rain' },
    55: { description: 'Dense drizzle', icon: 'rain' },
    61: { description: 'Slight rain', icon: 'rain' },
    63: { description: 'Moderate rain', icon: 'rain' },
    65: { description: 'Heavy rain', icon: 'rain' },
    71: { description: 'Slight snow', icon: 'snow' },
    73: { description: 'Moderate snow', icon: 'snow' },
    75: { description: 'Heavy snow', icon: 'snow' },
    80: { description: 'Slight rain showers', icon: 'rain' },
    81: { description: 'Moderate rain showers', icon: 'rain' },
    82: { description: 'Violent rain showers', icon: 'storm' },
    95: { description: 'Thunderstorm', icon: 'storm' },
    96: { description: 'Thunderstorm with hail', icon: 'storm' },
    99: { description: 'Severe thunderstorm', icon: 'storm' },
};

class WeatherService {
    private cache: Map<string, { data: WeatherData; expires: number }> = new Map();
    private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

    /**
     * Generate cache key from coordinates (rounded to 2 decimal places for efficiency)
     */
    private getCacheKey(lat: number, lng: number): string {
        return `${lat.toFixed(2)},${lng.toFixed(2)}`;
    }

    /**
     * Fetch current weather for a location
     */
    async getCurrentWeather(lat: number, lng: number): Promise<WeatherData | null> {
        const cacheKey = this.getCacheKey(lat, lng);

        // Check cache first
        const cached = this.cache.get(cacheKey);
        if (cached && cached.expires > Date.now()) {
            return cached.data;
        }

        try {
            const url = new URL('https://api.open-meteo.com/v1/forecast');
            url.searchParams.set('latitude', lat.toString());
            url.searchParams.set('longitude', lng.toString());
            url.searchParams.set('current', 'temperature_2m,relative_humidity_2m,precipitation,weather_code,wind_speed_10m,wind_direction_10m,visibility');
            url.searchParams.set('temperature_unit', 'fahrenheit');
            url.searchParams.set('wind_speed_unit', 'mph');
            url.searchParams.set('precipitation_unit', 'inch');
            url.searchParams.set('timezone', 'America/Chicago');

            const response = await fetch(url.toString());
            if (!response.ok) {
                console.error('Weather API error:', response.status);
                return null;
            }

            const data = await response.json();
            const current = data.current;

            const weatherCode = current.weather_code || 0;
            const wmoData = WMO_CODES[weatherCode] || { description: 'Unknown', icon: 'cloudy' as const };

            const isStormy = [82, 95, 96, 99].includes(weatherCode);
            const isSevere = [99].includes(weatherCode) || current.wind_speed_10m > 50;

            const weatherData: WeatherData = {
                temperature: Math.round(current.temperature_2m || 0),
                windSpeed: Math.round(current.wind_speed_10m || 0),
                windDirection: current.wind_direction_10m || 0,
                weatherCode,
                precipitation: current.precipitation || 0,
                humidity: current.relative_humidity_2m || 0,
                visibility: Math.round((current.visibility || 10000) / 1609.34), // Convert meters to miles
                isStormy,
                isSevere,
                description: wmoData.description,
                icon: wmoData.icon,
                timestamp: new Date()
            };

            // Cache the result
            this.cache.set(cacheKey, {
                data: weatherData,
                expires: Date.now() + this.CACHE_DURATION
            });

            return weatherData;

        } catch (error) {
            console.error('Failed to fetch weather:', error);
            return null;
        }
    }

    /**
     * Fetch weather for multiple locations (batch optimization)
     */
    async getWeatherForLocations(locations: Array<{ lat: number; lng: number; id: string }>): Promise<Map<string, WeatherData>> {
        const results = new Map<string, WeatherData>();

        // Deduplicate by rounding coordinates
        const uniqueLocations = new Map<string, { lat: number; lng: number; ids: string[] }>();

        for (const loc of locations) {
            const key = this.getCacheKey(loc.lat, loc.lng);
            const existing = uniqueLocations.get(key);
            if (existing) {
                existing.ids.push(loc.id);
            } else {
                uniqueLocations.set(key, { lat: loc.lat, lng: loc.lng, ids: [loc.id] });
            }
        }

        // Fetch in parallel with rate limiting (max 5 concurrent)
        const entries = Array.from(uniqueLocations.entries());
        const batchSize = 5;

        for (let i = 0; i < entries.length; i += batchSize) {
            const batch = entries.slice(i, i + batchSize);
            const promises = batch.map(async ([, loc]) => {
                const weather = await this.getCurrentWeather(loc.lat, loc.lng);
                if (weather) {
                    for (const id of loc.ids) {
                        results.set(id, weather);
                    }
                }
            });
            await Promise.all(promises);
        }

        return results;
    }

    /**
     * Get wind direction as compass bearing
     */
    getWindDirectionLabel(degrees: number): string {
        const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
        const index = Math.round(degrees / 22.5) % 16;
        return directions[index];
    }

    /**
     * Check if conditions are safe for field work
     */
    isFieldWorkSafe(weather: WeatherData): { safe: boolean; warnings: string[] } {
        const warnings: string[] = [];

        if (weather.isStormy) {
            warnings.push('⚡ Thunderstorm activity - seek shelter');
        }
        if (weather.windSpeed > 35) {
            warnings.push(`💨 High winds (${weather.windSpeed} mph) - use caution`);
        }
        if (weather.temperature > 105) {
            warnings.push(`🌡️ Extreme heat (${weather.temperature}°F) - heat safety protocol`);
        }
        if (weather.temperature < 20) {
            warnings.push(`❄️ Extreme cold (${weather.temperature}°F) - cold weather protocol`);
        }
        if (weather.visibility < 1) {
            warnings.push('🌫️ Low visibility - drive carefully');
        }

        return {
            safe: warnings.length === 0,
            warnings
        };
    }

    /**
     * Clear the cache (useful for testing)
     */
    clearCache(): void {
        this.cache.clear();
    }
}

export const weatherService = new WeatherService();
